import { Fragment, useEffect, useId, useLayoutEffect, useRef, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { Ico } from './Iconos.js';

/** How far the bubble keeps from the edge of the screen and from what it explains, in px. */
const MARGEN = 8;
const SEPARACION = 6;
/** The home screen's bubbles wait as long: a mouse crossing the lines lights none of them. */
const ESPERA_MS = 400;

/** One step of the sum a bubble works out with the period's own figures: «− Gastos  C$ 3,075.38». */
export interface PasoDelCalculo {
  signo?: '+' | '−' | '=';
  etiqueta: string;
  monto: string;
}

/**
 * Lo que quiere decir una línea de los libros y cómo sale, en un globo sobre ella (the owner,
 * 2026-10-17: «can we add informatic tooltips to the accounting page so users can see what every
 * line actually means and how it's calculated»).
 *
 * The home screen's bubble (`ConGlobo`) shows on hover alone, since a tap there opens a screen. A
 * line of the books opens nothing, so here a tap shows it too: the books are read on a phone as
 * often as on a laptop. The mouse resting on it brings it up after a moment and takes it away when
 * it leaves; a tap or a click keeps it up until the next tap anywhere, or Escape; the keyboard
 * brings it up on focus.
 *
 * Nothing is underlined. The words were, every one of them, and the owner could no longer tell a
 * heading from a line (2026-10-17: «everything is underlined i can't tell what parts are the
 * header of a category»). A heading or a figure the page works out says its words with
 * `ConMarca`, an ⓘ after them; a line of the books is the whole row, lit up under the mouse.
 *
 * Rendered into <body> and placed by hand, above the words or under them where there is no room,
 * and kept inside the screen: inside a card that has animated in, `fixed` is fixed to the card
 * (`Modal`), and inside a table that scrolls sideways an absolute bubble is cut off at its edge.
 * The button names it with `aria-describedby` while it shows, as `ConGlobo` does.
 */
export function Explicacion({
  texto,
  titulo,
  calculo,
  className = '',
  children,
}: {
  texto: string;
  /** Said first and in bold: what it is, an account with its code. */
  titulo?: string;
  /** The sum with the period's own figures, a line each under the words, as on a receipt. */
  calculo?: readonly PasoDelCalculo[];
  className?: string;
  children: ReactNode;
}) {
  const id = useId();
  const boton = useRef<HTMLButtonElement>(null);
  const globo = useRef<HTMLSpanElement>(null);
  /** The mouse resting on it, or the keyboard on it. */
  const [encima, setEncima] = useState(false);
  /** Tapped or clicked: up until the next tap. */
  const [fijo, setFijo] = useState(false);
  const [lugar, setLugar] = useState<{ top: number; left: number } | null>(null);
  const espera = useRef<number | undefined>(undefined);
  const visible = encima || fijo;

  useEffect(() => () => window.clearTimeout(espera.current), []);

  // Placed once it is drawn and its size is known, and again when anything under it scrolls.
  useLayoutEffect(() => {
    if (!visible) {
      setLugar(null);
      return;
    }
    const colocar = () => {
      const r = boton.current?.getBoundingClientRect();
      const g = globo.current?.getBoundingClientRect();
      if (!r || !g) return;
      const arriba = r.top - g.height - SEPARACION;
      setLugar({
        top: arriba >= MARGEN ? arriba : r.bottom + SEPARACION,
        left: Math.max(MARGEN, Math.min(r.left, window.innerWidth - g.width - MARGEN)),
      });
    };
    colocar();
    window.addEventListener('scroll', colocar, true);
    window.addEventListener('resize', colocar);
    return () => {
      window.removeEventListener('scroll', colocar, true);
      window.removeEventListener('resize', colocar);
    };
  }, [visible, texto, calculo]);

  // Escape puts any of them away, however it came up, without moving the mouse or the focus.
  useEffect(() => {
    if (!visible) return;
    const tecla = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      setFijo(false);
      setEncima(false);
    };
    window.addEventListener('keydown', tecla);
    return () => window.removeEventListener('keydown', tecla);
  }, [visible]);

  useEffect(() => {
    if (!fijo) return;
    const fuera = (e: PointerEvent) => {
      if (!boton.current?.contains(e.target as Node)) setFijo(false);
    };
    document.addEventListener('pointerdown', fuera, true);
    return () => document.removeEventListener('pointerdown', fuera, true);
  }, [fijo]);

  return (
    <>
      <button
        ref={boton}
        type="button"
        data-sin-icono="fila"
        data-explicacion
        aria-describedby={visible ? id : undefined}
        onClick={() => setFijo((f) => !f)}
        onPointerEnter={(e) => {
          if (e.pointerType !== 'mouse') return;
          window.clearTimeout(espera.current);
          espera.current = window.setTimeout(() => setEncima(true), ESPERA_MS);
        }}
        onPointerLeave={(e) => {
          if (e.pointerType !== 'mouse') return;
          window.clearTimeout(espera.current);
          setEncima(false);
        }}
        onFocus={(e) => {
          if (e.currentTarget.matches(':focus-visible')) setEncima(true);
        }}
        onBlur={() => {
          setEncima(false);
          setFijo(false);
        }}
        className={`group/explicacion cursor-help text-left transition ${className}`}
      >
        {children}
      </button>
      {visible &&
        createPortal(
          <span
            ref={globo}
            role="tooltip"
            id={id}
            data-explicacion-globo
            style={lugar ?? { top: 0, left: 0, visibility: 'hidden' }}
            className="pointer-events-none fixed z-30 w-max max-w-72 rounded-xl bg-slate-800 px-3 py-2.5 text-left text-xs leading-snug text-slate-300 shadow-lg shadow-black/40 ring-1 ring-white/10"
          >
            {titulo && <span className="mb-1 block text-sm font-semibold text-slate-100">{titulo}</span>}
            <span className="block">{texto}</span>
            {calculo && (
              <span
                data-explicacion-calculo
                className="mt-2 grid grid-cols-[0.75rem_1fr_auto] gap-x-1.5 gap-y-0.5 border-t border-white/10 pt-2 text-slate-100"
              >
                {calculo.map((p) => (
                  <Fragment key={p.etiqueta}>
                    <span className="text-slate-400">{p.signo}</span>
                    <span className={p.signo === '=' ? 'font-semibold' : ''}>{p.etiqueta}</span>
                    <span className={`tabular text-right ${p.signo === '=' ? 'font-semibold' : ''}`}>{p.monto}</span>
                  </Fragment>
                ))}
              </span>
            )}
          </span>,
          document.body,
        )}
    </>
  );
}

/**
 * A heading's words, or a worked-out figure's, with the ⓘ after them that says there is a bubble
 * to read here. The ⓘ is held to the last word, so a narrow screen wraps the words and never
 * leaves it alone on a line of its own; it lights with its `Explicacion`.
 */
export function ConMarca({ children: texto }: { children: string }) {
  const corte = texto.lastIndexOf(' ') + 1;
  return (
    <>
      {texto.slice(0, corte)}
      <span className="whitespace-nowrap">
        {texto.slice(corte)}
        <Ico
          icono="explicacion"
          className="ml-1 inline-block size-3.5 align-[-0.15em] text-slate-500 transition group-hover/explicacion:text-slate-300"
        />
      </span>
    </>
  );
}
